import { BoundingBox, CategorizedData, DisaggregatedMetric, GeoDetails, GeoTag, Metric, MetricMetadata } from "../services/types";

import populationSvg from '../assets/pop-blue.svg';
import globeSvg from '../assets/globe.svg';

import childrenSvg from '../assets/children.svg';
import insuranceSvg from '../assets/insurance.svg';
import childAdultSvg from '../assets/child-adult.svg';
import healthSvg from '../assets/health-insurance-icon 1.svg';

import hsGradSvg from '../assets/highschool-grad.svg';
import enrollmentSvg from '../assets/kindergarten.svg';

import incomeSvg from '../assets/medhhincome.svg';
import unemploymentSvg from '../assets/unemployment.svg';

import housingCostSvg from '../assets/cost-burdened.svg';
import homeownershipSvg from '../assets/homeownership.svg';
import turnoverSvg from '../assets/student-mobility.svg';
import vacantSvg from '../assets/house-outline.svg';
import houseSvg from '../assets/house.svg';

// Store our organization Id where we can access it later
export let orgId: number = 6;

// #region Organization Metric Mapping
const organizationMetrics: { [orgId: number]: any[] } =
{ 
    // Sacramento
    6:
    [
        {
            name: "Demographics",
            ids: [236, 237, 238, 314],
            order: [
                [237, 238],
            ],
            charts: [
                {
                    name: "race",
                    id: 237
                },
            ]
        },
        {
            name: "Accessibility",
            ids: [206, 210, 211, 212, 1191],
            order: [
                [211, 206, 212, 210],
            ],
            charts: [
                {
                    name: "commute",
                    id: 1191
                }
            ]
        },
        {
            name: "Child & family",
            ids: [223, 224, 225, 226, 229, 230, 1192, 1193, 1194],
            order: [
                [229, 225, 223, 226],
                [1192, 1193]
            ],
            charts: [
                {
                    name: "childrenage",
                    id: 1194
                },
                {
                    name: "singleparent",
                    id: 224
                }
            ]
        },
        {
            name: "Education",
            ids: [218, 219, 220, 221, 222],
            order: [
                [221, 218] 
            ],
            charts: [
                {
                    name: "ged",
                    id: 219
                },
                {
                    name: "bachelors",
                    id: 220
                },
                {
                    name: "nohs",
                    id: 222
                }
            ]
        },
        {
            name: "Economy",
            ids: [214, 216, 217, 1195, 1196, 1197, 1198, 1199, 1200, 1201, 1202, 1203],
            order: [
                [216, 214],
            ],
            charts: [
                {
                    name: "unemploy",
                    id: 216
                },
                {
                    name: "hhinc",
                    id: 1203
                }
            ]
        },
        {
            name: "Housing",
            ids: [230, 231, 232, 233, 234, 235],
            order: [
                [230, 235],
                [234, 233],
                [231, 232]
            ],
            charts: []
        }
    ],
    
};

// precisionMapping - maps metricId's to the desired number of significant figures for formatting metric values
export const precisionMapping: { [metricId: number]: number } = {
    206: 0,
    210: 0,
    211: 0,
    212: 0,
    224: 0,
    225: 0,
    226: 0,
    229: 0,
    231: 0,
    232: 0,
    1192: 0,
    1193: 0,
    238: 0,
    214: 0,
    216: 0,
    1195: 0,
    1196: 0,
    1197: 0,
    1198: 0,
    1199: 0,
    1200: 0,
    1201: 0,
    1202: 0,
    218: 0,
    219: 0,
    220: 0,
    221: 0,
    222: 0,
    233: 0,
    234: 0,
    235: 0,
};


const svgMap: { [orgId: number]: { [key: number]: string }} =
{
    // Sacramento
    6: {
        237: populationSvg,  // Population
        238: globeSvg, // Foreign Born
        229: childrenSvg, // Children under 5 in poverty
        225: insuranceSvg, // Children without health insurance
        223: childAdultSvg, // Child to Adult Ratio
        226: healthSvg, // Individuals without health insurance
        221: enrollmentSvg, // Age 3 and 4 school enrollment
        218: hsGradSvg, // 4-year high school graduation
        216: unemploymentSvg, // Unemployment rate
        214: incomeSvg, // Individuals earning more than 200% FPL
        231: housingCostSvg, // Housing Cost Burden
        235: homeownershipSvg, // Home ownership rate
        234: turnoverSvg, // Resident Turnover
        233: vacantSvg, // Vacant housing
        230: houseSvg, // Work hr/week to rent 2-bed home at min wage
    },
    
};

// There are svgs that need to be included in the pdf that are excluded in the dashboard
const pdfSvgMap: { [orgId: number]: { [key: number]: string }} =
{
    // Sacramento
    6: {
        237: populationSvg,  // Population
        238: globeSvg, // Foreign Born
        229: childrenSvg, // Children under 5 in poverty
        225: insuranceSvg, // Children without health insurance
        223: childAdultSvg, // Child to Adult Ratio
        226: healthSvg, // Individuals without health insurance
        221: enrollmentSvg, // Age 3 and 4 school enrollment
        218: hsGradSvg, // 4-year high school graduation
        216: unemploymentSvg, // Unemployment rate
        214: incomeSvg, // Individuals earning more than 200% FPL
        231: housingCostSvg, // Housing Cost Burden
        235: homeownershipSvg, // Home ownership rate
        234: turnoverSvg, // Resident Turnover
        233: vacantSvg, // Vacant housing
        230: houseSvg, // Work hr/week to rent 2-bed home at min wage
    },
    
};

// #endregion



// getMetricGroupIds - get the metric id values for a specific grouping 
export const getMetricGroupIds = (name: string) => {

    let ids = [];
    
    if(organizationMetrics[orgId] != undefined){
        const group: any = organizationMetrics[orgId].find((x: { name: string; }) => x.name === name);

        if(group != undefined){
            ids = group.ids;
        }
    }

    return ids;
}

// Store our organization Id where we can access it later
export const setOrgId = (id: number) => {
    orgId = id;
}

// Store our overlapping county fips codes for BLS and TRS data queries
export let countyId: string[] = [];
export const setCountyIds = (id: string[]) => {
    countyId = id;
}
export const getCountyIds = () => {
    return countyId;
}

// Store our bounding box so we can access it later
export let boundingBox: BoundingBox | null = null;
export const setBoundingBox = (bb: BoundingBox) => {
    boundingBox = bb;
}

// Store our excludeIds so we can access them later
export let excludeIds: string[] | null = null;
export const setExcludeIds = (ids: string[]) => {
    excludeIds = ids;
}

// getSvgMap - get our svg mapping for the given organization and metric id
export const getSvgMap = (id: number) => {
    let svg = "";
    if(svgMap[orgId] != undefined){
        svg = svgMap[orgId][id];
    }
    return svg;
}

// getPdfSvgMap - get our pdf svg mapping for the given organization and metric id
export const getPdfSvgMap = (id: number) => {
    let svg = "";
    if(pdfSvgMap[orgId] != undefined){
        svg = pdfSvgMap[orgId][id];
    }
    return svg;
}

// getMetricGroupOrderIds - get the ordering for a metric group
export const getMetricGroupOrderIds = (name: string) => {
    let orderIds = [];
    
    if(organizationMetrics[orgId] != undefined){
        const group: any = organizationMetrics[orgId].find((x: { name: string; }) => x.name === name);

        if(group != undefined){
            orderIds = group.order;
        }
    }

    return orderIds;
}

// getMetricGroupChartId - get a metric id given the group and chart key
export const getMetricGroupChartId = ( group: string, key: string) => {
    let id = -1;

    if(organizationMetrics[orgId] != undefined){
        const g = organizationMetrics[orgId].find((x: { name: string; }) => x.name === group);
        if(g != undefined){
            
            if(g.charts != undefined){

                const chart = g.charts.find((x: { name: string; }) => x.name === key);
                if(chart != undefined){
                    id = chart.id;
                }
            }
        }
    }

    return id;
}

export const categorizeData = (indicators: DisaggregatedMetric[]) => {

    const categories: { [key: string]: number[] } = {
        "Child & family": getMetricGroupIds("Child & family"),
        Education: getMetricGroupIds("Education"),
        Economy: getMetricGroupIds("Economy"), 
        Housing: getMetricGroupIds("Housing"),
        Accessibility: getMetricGroupIds("Accessibility"),
        Demographics: getMetricGroupIds("Demographics"),
    };

    // Initialize the categorizedData object dynamically with empty arrays for each category
    const categorizedData: Record<string, Metric[]> = Object.keys(categories).reduce((acc, category) => {
        acc[category] = [];
        return acc;
    }, {} as Record<string, Metric[]>);

    // Categorize the indicators dynamically
    indicators.forEach((item) => {
        Object.keys(categories).forEach((category) => {
            if (categories[category].includes(item.id)) {
                categorizedData[category].push(item);
            }
        });
    });

    return categorizedData;
};

export const fetchMetricCategories = async (orgId: number) => {
    if (!orgId) {
        console.log('No orgId provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getmetriccategories?orgId=${orgId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

export const fetchMetricMetadata = async (id: number) => {
    if (!id) {
        console.log('No id provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getmetricmetadata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

export const fetchMetricMetadataByOrg = async (orgId: number) => {
    if (!orgId) {
        console.log('No orgId provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getmetadatabyorg`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orgId }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

// fetch PercentileDist - get percentile distribution information by org and category
export const fetchPercentileDist = async (orgId: number, category: string) => {
    if (!orgId || !category) {
        console.log('No orgId or category provided to fetch metric percentile distributions');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getpercentiledist?orgId=${orgId}&category=${category}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

// fetchPercentileById - get percentile distribution information by metric id
export const fetchPercentileById = async (metricId: number) => {
    if (!metricId) {
        console.log('No metricId provided to fetch metric percentile data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getpercentilebymetric?metricId=${metricId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching percentile data:', error);
    }
};

// fetchDistributionData - get distribution data by metric id and selected area
export const fetchDistributionData = async (metricId: number, geoDetails: GeoDetails[], geoTag: GeoTag | null) => {
    if (!metricId) {
        console.log('No metricId provided to fetch indicator distribution data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getdistributiondata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, geoDetails, geoTag, geoEncoding: 'delta', allowDefault: true }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching indicator distribution data:', error);
    }
};

// fetchIndicatorBinCount - get indicator data and bin count by metric id and selected area
export const fetchIndicatorBinCount = async (metricId: number, geoDetails: GeoDetails[], geoTag: GeoTag | null) => {
    if (!metricId) {
        console.log('No metricId provided to fetch indicator distribution data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getindicatorbincounts`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, geoDetails, geoTag, geoEncoding: 'delta', allowDefault: true }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching indicator distribution data:', error);
    }
};

// fetchStateData - retreive state data for a selected metric using a prefix state code
export const fetchStateData = async (metricId: number, geoPrefix: string) => {
    if (!metricId || !geoPrefix) {
        console.log('No metricId or geoDetails provided to fetch metric state data');
        return;
    }

    console.log('geoprefix:', geoPrefix);

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getgeoprefixdata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, geoPrefix }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric state data:', error);
    }
};

// fetchNationalData - retreive national data for a selected metric
export const fetchNationalData = async (metricId: number) => {
    if (!metricId) {
        console.log('No metricId provided to fetch national metric data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getgeoprefixdata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching national metric data:', error);
    }
};

export const calculateGeoPrefix = (geoDetails: GeoDetails[]): string => {
    // Flatten the geoIds array
    const geoIds = geoDetails.flatMap(geo => geo.ids);

    // Helper function to get the first two digits of a geoId
    const getGeoPrefix = (geoId: string): string => geoId.substring(0, 2);

    // Count occurrences of each prefix
    const prefixCount: Record<string, number> = {};
    geoIds.forEach(geoId => {
        const prefix = getGeoPrefix(geoId);
        prefixCount[prefix] = (prefixCount[prefix] || 0) + 1;
    });

    // Find the prefix with the highest count
    return Object.entries(prefixCount).reduce((prev, curr) => 
        curr[1] > prev[1] ? curr : prev, ['', 0]
    )[0];
};


// getMissingMetrics - utility function to check for missing metrics on a given dashboard panel
export const getMissingMetrics = (
    tab: string,
    categorizedData: Metric[],
    indicators: DisaggregatedMetric[] | null
  ): string => {
    if (!indicators) return '';
  
    const missingMetrics = indicators?.filter(metric => 
        metric.category?.toLowerCase() === tab.toLowerCase() && 
        !categorizedData.some(data => data.id === metric.id)
    ) || [];    
  
    return missingMetrics.map(metric => metric.name).join(", ");
};

// Helper function to get and sort metrics by category
export const getSortedMetrics = (category: string, categorizedData: CategorizedData) => {
    const orders = getMetricGroupOrderIds(category);

    return orders.map((order: number[]) => 
        categorizedData[category]
            .filter(metric => order.includes(metric.id))
            .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    );
};

// Helper function to get and sort metrics by category - Used in PDF metrics display
export const getSortedMetricsPDF = (category: string, categorizedData : {
    category: string;
    metrics: DisaggregatedMetric[];
}[]): DisaggregatedMetric[] => {
    const metrics = categorizedData.find(data => data.category === category)?.metrics || [];
    const orders = getMetricGroupOrderIds(category);
    return orders.flatMap((order: number[]) =>
      metrics
        .filter(metric => order.includes(metric.id))
        .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    );
};
  
// Helper function to get the index metric data
export const getIndexMetricData = (
    category: string, 
    metadata: MetricMetadata[] | null, 
    disaggregatedData: DisaggregatedMetric[]
): { indexMetricId: number; indexMetricData: DisaggregatedMetric | null } => {
    
    const indexMetricId = metadata?.find(
        (metric) => 
            metric.metric_source_type === "index" && 
            metric.metric_category.toLowerCase() === category.toLowerCase()
    )?.id || 0;

    const indexMetricData = disaggregatedData.find(metric => metric.id === indexMetricId) || null;

    return { indexMetricId, indexMetricData };
};

// Helper function to get the index metric data - Formatted for PDF (MetricModal) use.
export const getIndexMetricDataPDF = (category: string, metadata: MetricMetadata[] | null, disaggregatedData: DisaggregatedMetric[]): DisaggregatedMetric | null => {
    const indexMetricId = metadata?.find(
        (metric) => metric.metric_source_type === "index" && metric.metric_category.toLowerCase() === category.toLowerCase()
    )?.id || 0;
    return disaggregatedData.find(metric => metric.id === Number(indexMetricId)) || null;
};


// CAPC SPECIFIC - Fetch county data for Children by Age Group
export const fetchAgeCountyData = async (orgId: number, metricId: number, geoTag: GeoTag | null): Promise<DisaggregatedMetric | null> => {
    if (!orgId) {
        console.log('No orgId provided to fetch county data for percent of children by age range');
        return null;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getindicatorbyid`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orgId, metricId, geoTag }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result?.data[0] || null;
    } catch (error: any) {
        console.error('Error fetching county data for percent of children by age range:', error);
        return null;
    }
};
